<script setup lang="ts">
import type { EventListItemDTO } from '~/shared/api/client/events/events.dto';
import { getEventCardStub } from '~/shared/lib/getEventStubImage';
import { resizeImage, resizeImageSrcsetWidths } from '~/shared/lib/resizeImage';
import { EVENT_CARD_IMAGE_WIDTHS } from '~/shared/lib/events-images.const';
import { useSpeakerDetailEvents } from '../lib/useSpeakerDetailEvents';

interface SpeakerEventItem {
  id: number;
  name: string;
  image: string;
  imageSrcset?: string;
  subject: string;
}

const { events } = useSpeakerDetailEvents();

// Cards block width by breakpoint, see SpeakerDetailPage
const BLOCK_WIDTH_DESKTOP = 860;
const BLOCK_WIDTH_TABLET = 683;
const BLOCK_WIDTH_TABLET_SMALL = 728;
const CARD_PADDING = 40;
// A 16:9 image needs this width to cover the 180px tall photo box
const MIN_IMAGE_WIDTH = 320;

function mapEvent(event: EventListItemDTO): SpeakerEventItem {
  const image = event.image?.photo.file_url ?? getEventCardStub(event.id);

  return {
    id: event.id,
    name: event.name,
    // The box ratio changes between breakpoints, so only scale down
    image: resizeImage(image, { width: 320 }),
    imageSrcset:
      resizeImageSrcsetWidths(image, EVENT_CARD_IMAGE_WIDTHS) || undefined,
    subject: event.format ?? '',
  };
}

const mappedEvents = computed(() => events.value.map(mapEvent));

// Cards share the row with flex: 1, so a single card takes the whole block
const imageSizes = computed(() => {
  const count = Math.max(mappedEvents.value.length, 1);
  const getWidth = (block: number) =>
    Math.max(Math.round(block / count - CARD_PADDING), MIN_IMAGE_WIDTH);

  return [
    '(max-width: 767px) 100vw',
    `(max-width: 1023px) ${getWidth(BLOCK_WIDTH_TABLET_SMALL)}px`,
    `(max-width: 1379px) ${getWidth(BLOCK_WIDTH_TABLET)}px`,
    `${getWidth(BLOCK_WIDTH_DESKTOP)}px`,
  ].join(', ');
});
</script>

<template>
  <div v-if="mappedEvents.length" :class="$style.section">
    <div :class="$style.block">
      <div :class="$style.header">
        <h3 :class="$style.title">Ближайшие мероприятия</h3>
        <NuxtLink to="/events" :class="$style['header__view-all']">
          Смотреть все
        </NuxtLink>
      </div>
      <div :class="$style.cards">
        <NuxtLink
          v-for="event in mappedEvents"
          :key="event.id"
          :to="`/events/${event.id}`"
          :class="$style.card"
        >
          <div :class="$style.photo">
            <img
              :src="event.image"
              :srcset="event.imageSrcset"
              :sizes="imageSizes"
              :alt="event.name"
              :class="$style.img"
            />
          </div>
          <div :class="$style.info">
            <span v-if="event.subject" :class="$style.category">
              {{ event.subject }}
            </span>
            <span :class="$style.name">{{ event.name }}</span>
          </div>
        </NuxtLink>
      </div>
    </div>
  </div>
</template>

<style module lang="scss">
.section {
  padding-bottom: var(--space-5x);
}

.block {
  background-color: var(--color-gray-bg);
  overflow: hidden;
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 64px;
  padding: 0 var(--space-2-5x);
  border-bottom: 1px solid var(--color-gray-line);
}

.title {
  @include text_h5;
  color: var(--color-black-1);
  margin: 0;
}

.header__view-all {
  @include text_button3;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  padding: 7px 20px;
  background-color: var(--color-white);
  border: 1px solid var(--color-gray-line);
  color: var(--color-black-1);
  text-decoration: none;
  white-space: nowrap;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.85;
  }
}

.cards {
  display: flex;
}

.card {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: var(--space-1-5x);
  padding: var(--space-2-5x);
  text-decoration: none;
  border-top: 1px solid var(--color-gray-line);
  transition: opacity 0.2s;

  &:not(:first-child) {
    border-left: 1px solid var(--color-gray-line);
  }

  &:hover {
    opacity: 0.85;
  }
}

.photo {
  width: 100%;
  height: 180px;
  overflow: hidden;
}

.img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.info {
  display: flex;
  flex-direction: column;
  gap: var(--space-1x);
}

.category {
  @include text_caption2_caps;
  color: var(--color-gray-text);
}

.name {
  @include text_body2;
  color: var(--color-black-1);
  @include line_clamp(3);
}

@media (max-width: 767px) {
  .cards {
    flex-wrap: wrap;
  }

  .card {
    flex: 1 0 40%;

    &:nth-child(3) {
      flex-basis: 100%;
      border-left: none;
    }
  }
}

@media (max-width: 599px) {
  .title {
    font-size: 18px;
  }
}

@media (max-width: 479px) {
  .card {
    &:not(:first-child) {
      display: none;
    }
  }

  .title {
    font-size: 14px;
  }
}
</style>
